import axios from 'axios';
import { API_URL } from '../config';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

class CaseStudyService {
  async getAllCaseStudies(params = {}) {
    const response = await axios.get(`${API_URL}/api/case-studies`, { 
      ...getAuthHeaders(),
      params 
    });
    return response.data;
  }

  async getCaseStudyById(id) {
    const response = await axios.get(`${API_URL}/api/case-studies/${id}`, getAuthHeaders());
    return response.data.data;
  }

  async getCaseStudyBySlug(slug) {
    const response = await axios.get(`${API_URL}/api/case-studies/slug/${slug}`);
    return response.data.data;
  }

  async createCaseStudy(data) {
    const response = await axios.post(`${API_URL}/api/case-studies`, data, getAuthHeaders());
    return response.data.data.caseStudy;
  }

  async updateCaseStudy(id, data) {
    const response = await axios.put(`${API_URL}/api/case-studies/${id}`, data, getAuthHeaders());
    return response.data.data.caseStudy;
  }

  async deleteCaseStudy(id) {
    const response = await axios.delete(`${API_URL}/api/case-studies/${id}`, getAuthHeaders());
    return response.data;
  }

  async archiveCaseStudy(id) {
    const response = await axios.patch(`${API_URL}/api/case-studies/${id}/archive`, {}, getAuthHeaders());
    return response.data.data;
  }

  async toggleStatus(id, currentStatus) {
    const response = await axios.patch(
      `${API_URL}/api/case-studies/${id}/status`,
      { status: currentStatus === 'draft' ? 'published' : 'draft' },
      getAuthHeaders()
    );
    return response.data.data;
  }

  async getAllPublishedCaseStudies(params = {}) {
    const response = await axios.get(`${API_URL}/api/case-studies/public`, { params });
    return {
      data: response.data.data,
      total: response.data.pagination.total,
      pages: response.data.pagination.pages,
      currentPage: response.data.pagination.page
    };
  }

  async getCaseStudyStats() {
    const response = await axios.get(`${API_URL}/api/case-studies/stats`, getAuthHeaders());
    return response.data.data;
  }

  async incrementViews(id) {
    const response = await axios.post(`${API_URL}/api/case-studies/${id}/views`, {}, getAuthHeaders());
    return response.data.data;
  }

  async incrementLikes(id) {
    const response = await axios.post(`${API_URL}/api/case-studies/${id}/likes`, {}, getAuthHeaders());
    return response.data.data;
  }

  async incrementShares(id) {
    const response = await axios.post(`${API_URL}/api/case-studies/${id}/shares`, {}, getAuthHeaders());
    return response.data.data;
  }

  async exportToCSV(caseStudies) {
    try {
      const headers = ['Title', 'URL Slug', 'Description', 'Client', 'Status', 'Published At', 'Created At'];
      const rows = caseStudies.map(caseStudy => [
        caseStudy.title,
        caseStudy.urlSlug,
        caseStudy.description,
        caseStudy.client?.name,
        caseStudy.status,
        caseStudy.publishedAt ? new Date(caseStudy.publishedAt).toISOString() : '',
        new Date(caseStudy.createdAt).toISOString()
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `case-studies_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting case studies to CSV:', error);
      throw error;
    }
  }

  async exportToJSON(caseStudies) {
    try {
      const jsonContent = JSON.stringify(caseStudies, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `case-studies_${new Date().toISOString()}.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting case studies to JSON:', error);
      throw error;
    }
  }

  async importCaseStudies(caseStudies, mode = 'merge') {
    try {
      const response = await axios.post(
        `${API_URL}/api/case-studies/import`,
        { caseStudies, mode },
        getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error importing case studies:', error);
      throw error.response?.data || error;
    }
  }
}

export const caseStudyService = new CaseStudyService(); 